package com.mgacreative.hotel.shared.ui.designsystem.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import org.jetbrains.compose.resources.stringResource
import hotelos.shared.ui.designsystem.generated.resources.Res
import hotelos.shared.ui.designsystem.generated.resources.*
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

data class NavigationMenuItem(
    val title: String,
    val iconName: String = "",
    val imageVector: androidx.compose.ui.graphics.vector.ImageVector? = null,
    val isDestructive: Boolean = false,
    val onClick: () -> Unit
)

@Composable
fun HFNavigationDrawer(
    userName: String = "sales user",
    userRole: String = "Admin",
    userRoles: List<String> = emptyList(),
    allowedModules: List<String> = listOf("FRONT_DESK", "ROOM_STATUS", "STAFF", "SYSTEM"),
    headerIcon: (@Composable () -> Unit)? = null,
    onLogoutClick: () -> Unit,
    onNavigate: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val normalizedRole = userRole.uppercase()
    val isSuperUser = (normalizedRole == "SUPER ADMIN" || 
                      normalizedRole == "SUPER_ADMIN" || 
                      normalizedRole == "HOTEL_OWNER" || 
                      userRoles.any { it.equals("SUPER ADMIN", ignoreCase = true) || it.equals("SUPER_ADMIN", ignoreCase = true) || it.equals("HOTEL_OWNER", ignoreCase = true) })

    fun hasModule(moduleKey: String): Boolean {
        if (isSuperUser) return true
        return allowedModules.any { it.equals(moduleKey, ignoreCase = true) }
    }

    val menuItems = mutableListOf<NavigationMenuItem>().apply {
        add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Gösterge Paneli")) { onNavigate("DashboardScreen") })

        // 1. Ön Büro (Default)
        if (hasModule("FRONT_DESK")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Ön Büro (Rezervasyon)")) { onNavigate("NewReservationCalendarScreen") })
        }
        // 2. Oda Durumları (Default)
        if (hasModule("ROOM_STATUS")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Oda Durumları")) { onNavigate("RoomStatusScreen") })
        }
        // 3. Kat Hizmetleri
        if (hasModule("HOUSEKEEPING")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Kat Hizmetleri")) { onNavigate("HousekeepingScreen") })
        }
        // 4. Muhasebe & Finans
        if (hasModule("ACCOUNTING")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Kasa & Finans")) { onNavigate("CashRegisterScreen") })
        }
        // 5. Restoran POS
        if (hasModule("RESTAURANT")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Restoran POS")) { onNavigate("RestaurantPosScreen") })
        }
        // 6. Envanter
        if (hasModule("INVENTORY")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Envanter")) { onNavigate("InventoryScreen") })
        }
        // 7. Personel Yönetimi (Default)
        if (hasModule("STAFF")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Personel Yönetimi")) { onNavigate("ShiftPlannerScreen") })
        }
        // 8. OTA Kanal Yönetimi
        if (hasModule("CHANNEL_MANAGER")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Kanal Yöneticisi (OTA)")) { onNavigate("ChannelManagerScreen") })
        }
        // 9. Sistem Yönetimi (Default)
        if (hasModule("SYSTEM")) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Sistem Yönetimi")) { onNavigate("AdminDashboardScreen") })
        }

        // Yönetici için Üye Yönetimi Paneli
        val isSystemAdmin = isSuperUser || 
                            userRole.equals("ADMIN", ignoreCase = true) ||
                            userRoles.any { it.equals("ADMIN", ignoreCase = true) }
        if (isSystemAdmin) {
            add(NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Üye Yönetimi")) { onNavigate("AdminMembersScreen") })
        }
    }


    Column(
        modifier = modifier
            .fillMaxHeight()
            .width(260.dp)
            .background(Color.White)
    ) {
        // Corporate Brand Header
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 20.dp, vertical = 20.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "HotelOS",
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF0F172A),
                letterSpacing = (-0.5).sp
            )
        }
        HorizontalDivider(color = Color(0xFFF1F5F9), modifier = Modifier.padding(horizontal = 16.dp))

        // Main Navigation List (Text only, corporate SaaS layout)
        LazyColumn(
            modifier = Modifier
                .weight(1f)
                .padding(vertical = 8.dp)
        ) {
            items(menuItems) { item ->
                DrawerMenuItem(
                    item = item,
                    textColor = Color(0xFF334155),
                    fontWeight = FontWeight.Medium
                )
            }
        }

        // Bottom Footer (Secondary Actions, Language & Logout)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 16.dp)
        ) {
            HorizontalDivider(
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 6.dp),
                color = Color(0xFFE2E8F0)
            )

            // Dynamic Language Selector Row
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 20.dp, vertical = 4.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Dil / Language"),
                    fontSize = 13.sp,
                    color = Color(0xFF64748B),
                    fontWeight = FontWeight.Medium
                )
                LanguageSelectorDropdown()
            }

            DrawerMenuItem(
                item = NavigationMenuItem("HotelOS Gold") { onNavigate("SubscriptionScreen") },
                textColor = Color(0xFF475569),
                fontWeight = FontWeight.Normal
            )
            DrawerMenuItem(
                item = NavigationMenuItem(com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Yardım & Destek")) { onNavigate("HelpScreen") },
                textColor = Color(0xFF475569),
                fontWeight = FontWeight.Normal
            )
            DrawerMenuItem(
                item = NavigationMenuItem(
                    title = com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n("Oturumu Kapat"),
                    isDestructive = true,
                    onClick = onLogoutClick
                ),
                textColor = Color(0xFFDC2626),
                fontWeight = FontWeight.SemiBold
            )
        }
    }
}

@Composable
fun DrawerMenuItem(
    item: NavigationMenuItem,
    textColor: Color = Color(0xFF334155),
    fontWeight: FontWeight = FontWeight.Medium
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 2.dp)
            .clip(RoundedCornerShape(8.dp))
            .clickable { item.onClick() }
            .padding(horizontal = 14.dp, vertical = 10.dp)
    ) {
        Text(
            text = item.title,
            color = textColor,
            fontWeight = fontWeight,
            fontSize = 14.sp
        )
    }
}
